import { Box, Stack, Typography } from '@mui/material';
import {
  getMediaBasePath,
  ImageWrapper,
  TypographyHtml,
} from '@geneo2-web/shared-ui';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IQuestionMetadataSolutionStep } from '../../api/learners';

export interface DisplayAnswer {
  text?: { optionText?: string };
  image?: { image?: { imageUrl?: string; imageName?: string } };
  isCorrect?: QAttemptResultEnum;
}

export type OptionReviewStatus =
  | 'userCorrect'
  | 'userWrong'
  | 'missed'
  | 'neutral';

export interface ReviewOption {
  answer: DisplayAnswer;
  status: OptionReviewStatus;
  feedback?: string;
}

const OPTION_STATUS_STYLES: Record<
  OptionReviewStatus,
  {
    bgcolor: string;
    borderColor: string;
    iconBg: string;
    iconColor: string;
  }
> = {
  userCorrect: {
    bgcolor: '#f0fdf4',
    borderColor: '#1dc65e',
    iconBg: '#1dc65e',
    iconColor: '#fff',
  },
  userWrong: {
    bgcolor: '#fef2f2',
    borderColor: '#ef4444',
    iconBg: '#ef4444',
    iconColor: '#fff',
  },
  missed: {
    bgcolor: '#fffbeb',
    borderColor: '#f59e0b',
    iconBg: '#f59e0b',
    iconColor: '#fff',
  },
  neutral: {
    bgcolor: '#fff',
    borderColor: '#e2e8f0',
    iconBg: 'transparent',
    iconColor: '#94a3b8',
  },
};

export const AnswerDisplayRow: React.FC<{
  answer: DisplayAnswer;
  variant: 'submitted' | 'correct';
  showMissedBadge?: boolean;
}> = ({ answer, variant, showMissedBadge }) => {
  const { t } = useTranslation();
  const isCorrect =
    variant === 'correct' ||
    answer.isCorrect === QAttemptResultEnum.RESPONSE_CORRECT;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: isCorrect ? '#dcfce7' : '#fef2f2',
        borderRadius: '8px',
        px: 2,
        py: 1.25,
        border: `1px solid ${isCorrect ? '#22c55e' : '#ef4444'}`,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isCorrect ? '#16a34a' : '#ef4444',
        }}
      >
        {isCorrect ? (
          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
        ) : (
          <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!!answer.image && (
          <ImageWrapper
            path={getMediaBasePath(
              answer.image?.image?.imageUrl,
              'processedMediaBucket'
            )}
            name={answer.image?.image?.imageName || 'image'}
            styles={{ width: '80%' }}
          />
        )}
        {!!answer.text && (
          <TypographyHtml
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#1e293b',
              lineHeight: 1.4,
            }}
          >
            {answer.text?.optionText || ''}
          </TypographyHtml>
        )}
      </Box>
      {showMissedBadge && (
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            color: '#dc2626',
            bgcolor: '#fee2e2',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            flexShrink: 0,
            lineHeight: 1.6,
          }}
        >
          {t('PAL_AR_MISSED')}
        </Typography>
      )}
    </Box>
  );
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  letterSpacing: 1,
  textTransform: 'uppercase' as const,
};

export const OptionReviewRow: React.FC<{ option: ReviewOption }> = ({
  option,
}) => {
  const { t } = useTranslation();
  const { answer, status, feedback } = option;
  const styles = OPTION_STATUS_STYLES[status];
  const showYourAnswerBadge =
    status === 'userCorrect' || status === 'userWrong';
  const showMissedBadge = status === 'missed';

  return (
    <Box
      sx={{
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        bgcolor: styles.bgcolor,
        border: `1.5px solid ${styles.borderColor}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: styles.iconBg,
            mt: 0.125,
          }}
        >
          {status === 'neutral' ? (
            <RadioButtonUncheckedIcon
              sx={{ fontSize: 18, color: styles.iconColor }}
            />
          ) : status === 'userWrong' ? (
            <CloseIcon sx={{ fontSize: 16, color: styles.iconColor }} />
          ) : (
            <CheckIcon sx={{ fontSize: 16, color: styles.iconColor }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.75,
              mb: feedback ? 0.75 : 0,
            }}
          >
            {!!answer.image && (
              <ImageWrapper
                path={getMediaBasePath(
                  answer.image?.image?.imageUrl,
                  'processedMediaBucket'
                )}
                name={answer.image?.image?.imageName || 'image'}
                styles={{ width: '80%' }}
              />
            )}
            {!!answer.text && (
              <TypographyHtml
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#1e293b',
                  lineHeight: 1.4,
                }}
              >
                {answer.text?.optionText || ''}
              </TypographyHtml>
            )}
            {showYourAnswerBadge && (
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#5b21b6',
                  bgcolor: '#ede9fe',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  flexShrink: 0,
                  lineHeight: 1.6,
                }}
              >
                {t('PAL_AR_YOUR_ANSWER')}
              </Typography>
            )}
            {showMissedBadge && (
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#92400e',
                  bgcolor: '#fef3c7',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  flexShrink: 0,
                  lineHeight: 1.6,
                }}
              >
                {t('PAL_AR_MISSED')}
              </Typography>
            )}
          </Box>
          {feedback && (
            <TypographyHtml
              sx={{
                fontSize: 13,
                color: status === 'userWrong' ? '#991b1b' : '#64748b',
                fontStyle: 'italic',
                lineHeight: 1.55,
              }}
            >
              {feedback}
            </TypographyHtml>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const AllOptionsSection: React.FC<{
  options: ReviewOption[];
  embedded?: boolean;
}> = ({ options, embedded }) => {
  const { t } = useTranslation();
  if (options.length === 0) return null;

  return (
    <Box sx={{ py: embedded ? 0 : 3, borderBottom: embedded ? 'none' : '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
          📋
        </Box>
        <Typography sx={sectionLabelSx}>{t('PAL_AR_ALL_OPTIONS')}</Typography>
      </Box>
      <Stack spacing={1.25}>
        {options.map((option, index) => (
          <OptionReviewRow key={'option_' + index} option={option} />
        ))}
      </Stack>
    </Box>
  );
};

export const UserAnswerSection: React.FC<{
  answers: DisplayAnswer[];
  embedded?: boolean;
}> = ({ answers, embedded }) => {
  const { t } = useTranslation();
  if (answers.length === 0) return null;
  return (
    <Box sx={{ py: embedded ? 0 : 3, borderBottom: embedded ? 'none' : '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
          🧑
        </Box>
        <Typography sx={sectionLabelSx}>{t('PAL_AR_YOUR_ANSWER')}</Typography>
      </Box>
      <Stack spacing={1}>
        {answers.map((val, index) => (
          <AnswerDisplayRow
            key={'submitted_' + index}
            answer={val}
            variant="submitted"
          />
        ))}
      </Stack>
    </Box>
  );
};

export const CorrectAnswerSection: React.FC<{
  correctAnswers: DisplayAnswer[][];
  userSubmittedAnswers?: DisplayAnswer[];
  embedded?: boolean;
}> = ({ correctAnswers, userSubmittedAnswers, embedded }) => {
  const { t } = useTranslation();
  if (correctAnswers.length === 0) return null;

  const submittedTextsSet = useMemo(
    () =>
      new Set(
        (userSubmittedAnswers ?? [])
          .map((a) => a.text?.optionText?.trim().toLowerCase())
          .filter(Boolean)
      ),
    [userSubmittedAnswers]
  );

  return (
    <Box sx={{ py: embedded ? 0 : 3, borderBottom: embedded ? 'none' : '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CheckIcon sx={{ fontSize: 16, color: '#16a34a' }} />
        <Typography sx={sectionLabelSx}>
          {t('PAL_AR_CORRECT_ANSWER')}
        </Typography>
      </Box>
      <Stack spacing={1}>
        {correctAnswers.map((group, index) => (
          <React.Fragment key={'correct_group_' + index}>
            <Box>
              {group.map((val, idx) => {
                const optText =
                  val.text?.optionText?.trim().toLowerCase() || '';
                const wasMissed =
                  optText !== '' && !submittedTextsSet.has(optText);
                return (
                  <Box
                    key={'corr_' + index + '_' + idx}
                    sx={{ mb: idx !== group.length - 1 ? 0.75 : 0 }}
                  >
                    <AnswerDisplayRow
                      answer={val}
                      variant="correct"
                      showMissedBadge={wasMissed}
                    />
                  </Box>
                );
              })}
            </Box>
            {index !== correctAnswers.length - 1 && (
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#94a3b8',
                  my: 0.75,
                }}
              >
                {t('PAL_AR_OR')}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export const SolutionSection: React.FC<{
  solution: IQuestionMetadataSolutionStep[];
}> = ({ solution }) => {
  const { t } = useTranslation();
  if (solution.length === 0) return null;
  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
          📚
        </Box>
        <Typography sx={sectionLabelSx}>
          {t('PAL_AR_SOLUTION_EXPLAINED')}
        </Typography>
      </Box>
      <Stack spacing={3}>
        {solution.map((step, index) => (
          <Box
            key={'solution_' + index}
            sx={{
              pl: 2,
              borderLeft: '3px solid #22c55e',
              bgcolor: '#f8fafc',
              borderRadius: '8px',
              py: 1,
            }}
          >
            {step.description && (
              <TypographyHtml
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 0.75,
                }}
              >
                {step.description}
              </TypographyHtml>
            )}
            {step.explanation && (
              <TypographyHtml
                sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}
              >
                {step.explanation}
              </TypographyHtml>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
